import { useState, useMemo } from 'react';
import {
  Stack,
  Paper,
  Table,
  Text,
  Group,
  Badge,
  TextInput,
  Skeleton,
} from '@mantine/core';
import { IconSearch, IconTruck } from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import EmptyState from '@/components/EmptyState';
import { useDeliverySchedule as useDeliveryScheduleApi } from '@/hooks/usePortalApi';
import dayjs from 'dayjs';

interface DeliveryScheduleItem {
  id: number;
  scheduled_date: string;
  route_code: string;
  estimated_arrival: string;
  status: string;
}

type SortField = 'scheduled_date' | 'route_code' | 'estimated_arrival' | 'status';

export default function DeliverySchedule() {
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('scheduled_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const { data, isLoading } = useDeliveryScheduleApi();
  const items: DeliveryScheduleItem[] = (data?.items ?? []) as DeliveryScheduleItem[];

  const filtered = useMemo(() => {
    let result = [...items];
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(
        (r) =>
          r.route_code.toLowerCase().includes(s) ||
          r.status.toLowerCase().includes(s) ||
          r.scheduled_date.toLowerCase().includes(s),
      );
    }
    result.sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[sortField];
      const bv = (b as unknown as Record<string, unknown>)[sortField];
      const cmp = (av as string) < (bv as string) ? -1 : (av as string) > (bv as string) ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [items, search, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIndicator = (field: SortField) =>
    sortField === field ? (sortDir === 'asc' ? ' \u2191' : ' \u2193') : '';

  const getDateProximityBadge = (scheduledDate: string) => {
    const daysUntil = dayjs(scheduledDate).diff(dayjs(), 'day');
    if (daysUntil === 0) {
      return (
        <Badge size="xs" variant="filled" color="blue">
          today
        </Badge>
      );
    }
    if (daysUntil === 1) {
      return (
        <Badge size="xs" variant="light" color="blue">
          tomorrow
        </Badge>
      );
    }
    if (daysUntil > 1 && daysUntil <= 3) {
      return (
        <Badge size="xs" variant="light" color="cyan">
          in {daysUntil} days
        </Badge>
      );
    }
    return null;
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title="Delivery Schedule"
        description="Upcoming DSD deliveries for your location"
      />

      <TextInput
        placeholder="Search by route code or status..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />

      {isLoading ? (
        <Stack gap="xs">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} height={50} radius="md" />
          ))}
        </Stack>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<IconTruck size={30} />}
          title="No upcoming deliveries"
          description={
            search
              ? 'No deliveries match your search criteria'
              : 'There are no scheduled deliveries at this time'
          }
        />
      ) : (
        <Paper>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('scheduled_date')}
                >
                  Scheduled Date{sortIndicator('scheduled_date')}
                </Table.Th>
                <Table.Th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('route_code')}
                >
                  Route Code{sortIndicator('route_code')}
                </Table.Th>
                <Table.Th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('estimated_arrival')}
                >
                  Estimated Arrival{sortIndicator('estimated_arrival')}
                </Table.Th>
                <Table.Th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('status')}
                >
                  Status{sortIndicator('status')}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>
                    <Group gap="xs">
                      <Text size="sm" fw={500}>
                        {dayjs(row.scheduled_date).format('ddd, MMM D, YYYY')}
                      </Text>
                      {getDateProximityBadge(row.scheduled_date)}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="outline" size="sm">
                      {row.route_code}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {dayjs(row.estimated_arrival).format('h:mm A')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <StatusBadge status={row.status} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
